import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyLiraIcon from '@mui/icons-material/CurrencyLira';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import DeleteIcon from '@mui/icons-material/Delete';
import EuroIcon from '@mui/icons-material/Euro';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SaveIcon from '@mui/icons-material/Save';

import {
  ACTION_SX,
  BRAND,
  C,
  cancelButton,
  neutralButton,
  primaryButton,
  sayiyaCevir
} from './teklifTheme';
import { getKatalog, getTeklifDetay, teklifKaydet, toplamlariHesapla } from './teklifApi';
import { PARA_BIRIMLERI, getKurlar, tldenCevir, tutarFormatla } from './kurServisi';

const PARA_IKONLARI = {
  CurrencyLira: CurrencyLiraIcon,
  AttachMoney: AttachMoneyIcon,
  Euro: EuroIcon,
  CurrencyPound: CurrencyPoundIcon
};

const BOS_TEKLIF = {
  TeklifId: 0,
  TeklifNo: '',
  ProjeAdi: '',
  MusteriAdSoyad: '',
  MusteriTelefon: '',
  MusteriAdres: '',
  BayiId: null,
  ServisId: null,
  Durum: 1,
  GecerlilikGun: 15,
  ParaBirimi: 'TRY',
  DovizKuru: 1,
  SarfMalzemeOrani: 20,
  IndirimOrani: 0,
  KdvOrani: 20,
  Sartlar: '',
  Kalemler: []
};

const styles = {
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    padding: '16px 24px',
    borderBottom: `1px solid ${C.borderSoft}`,
    backgroundColor: C.surface
  },
  titleIcon: {
    width: 40,
    height: 40,
    borderRadius: '12px',
    display: 'grid',
    placeItems: 'center',
    color: '#ffffff',
    flexShrink: 0,
    background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.hover} 100%)`
  },
  content: { padding: '24px', backgroundColor: C.headBg },
  section: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: C.inkMuted,
    marginBottom: '14px'
  },
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: C.surface,
      '& fieldset': { borderColor: C.border },
      '&:hover fieldset': { borderColor: `${BRAND.main}99` },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${BRAND.main}29` },
      '&.Mui-focused fieldset': { borderColor: BRAND.main }
    }
  },
  headCell: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    padding: '10px 12px'
  },
  bodyCell: {
    padding: '8px 12px',
    fontSize: '0.875rem',
    color: C.inkBody,
    borderBottom: `1px solid ${C.borderSoft}`
  },
  ozetSatir: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    fontSize: '0.875rem',
    color: C.inkBody
  },
  ozetToplam: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    marginTop: '8px',
    borderRadius: '12px',
    backgroundColor: `${BRAND.main}1f`,
    border: `1px solid ${BRAND.main}4d`
  },
  actions: {
    padding: '16px 24px',
    gap: '8px',
    borderTop: `1px solid ${C.borderSoft}`,
    backgroundColor: C.surface
  }
};

function OfferDialog({
  open,
  mode,
  teklifId,
  kopyaKaynakId,
  bayiler,
  servisler,
  onClose,
  onSaved
}) {
  const guncellemeMi = mode === 'update';
  const kopyaMi = !guncellemeMi && Number(kopyaKaynakId) > 0;

  const [form, setForm] = useState(BOS_TEKLIF);
  const [katalog, setKatalog] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [hatalar, setHatalar] = useState({});

  // TCMB kurlari: { kurlar, tarih, kaynak }
  const [kurBilgisi, setKurBilgisi] = useState({ kurlar: { TRY: 1 }, tarih: '', kaynak: '' });
  const [kurUyarisi, setKurUyarisi] = useState('');

  // Katalog bir kez cekiliyor.
  useEffect(() => {
    if (!open) return;
    getKatalog()
      .then(setKatalog)
      .catch(() => setHata('Kalem kataloğu yüklenemedi.'));
  }, [open]);

  // Gunluk kurlar. kurServisi onbellekli, tekrar tekrar istek atmaz.
  useEffect(() => {
    if (!open) return;
    getKurlar().then(bilgi => {
      setKurBilgisi(bilgi);
      if (bilgi.kaynak === 'varsayilan') {
        setKurUyarisi(
          'TCMB kur servisine ulaşılamadı; varsayılan kurlar kullanılıyor. Kuru elle kontrol ediniz.'
        );
      }
    });
  }, [open]);

  /** Secili para biriminin TCMB guncel kuru. Kullanici bunun USTUNE cikamaz. */
  const guncelKur = kurBilgisi.kurlar[form.ParaBirimi] || 1;
  const tlMi = form.ParaBirimi === 'TRY';

  // Dialog her acilista sifirlanir; guncellemede detay cekilir.
  useEffect(() => {
    if (!open) return;

    setHata('');
    setHatalar({});

    if (!guncellemeMi && !kopyaMi) {
      setForm({ ...BOS_TEKLIF, Kalemler: [] });
      return;
    }

    setYukleniyor(true);
    getTeklifDetay(guncellemeMi ? teklifId : kopyaKaynakId)
      .then(detay => {
        if (!detay) {
          setHata('Teklif detayı bulunamadı.');
          return;
        }

        if (guncellemeMi) {
          setForm(detay);
          return;
        }

        // Kopya: kalemler ve oranlar korunur, kimlik ve durum sifirlanir.
        setForm({
          ...detay,
          TeklifId: 0,
          TeklifNo: '',
          Durum: 1,
          ProjeAdi: `${detay.ProjeAdi || ''} (kopya)`.trim(),
          Kalemler: (detay.Kalemler || []).map((k, i) => ({
            ...k,
            SatirId: Date.now() + i
          }))
        });
      })
      .catch(() => setHata('Teklif detayı yüklenemedi. Lütfen tekrar deneyin.'))
      .finally(() => setYukleniyor(false));
  }, [open, guncellemeMi, kopyaMi, teklifId, kopyaKaynakId]);

  const alanDegistir = (alan, deger) => {
    setForm(onceki => ({ ...onceki, [alan]: deger }));
    if (hatalar[alan]) setHatalar(onceki => ({ ...onceki, [alan]: '' }));
  };

  // -------------------------------------------------------- PARA BIRIMI / KUR

  /**
   * Para birimi degisince, TCMB'nin O GUN YAYINLADIGI kur dogrudan alana yazilir.
   * Kullanici kontrol edip elle duzeltebilir (asagi dogru; tavan guncel kur).
   *
   * Kurlar henuz gelmediyse alan 1'e dusurulmez; kullanici yanlis kurla teklif
   * hazirlamasin diye bos birakilir.
   */
  const paraBirimiDegistir = kod => {
    const yayinlanan = kurBilgisi.kurlar[kod];
    setKurUyarisi('');

    if (kod === 'TRY') {
      setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: 1 }));
      return;
    }

    if (!yayinlanan) {
      setKurUyarisi('Güncel kur henüz alınamadı. Kuru elle giriniz.');
      setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: '' }));
      return;
    }

    setForm(onceki => ({ ...onceki, ParaBirimi: kod, DovizKuru: yayinlanan }));
  };

  /**
   * Kur elle degistirilebilir ama TCMB guncel kurunun USTUNE cikamaz.
   * Asan deger girilirse guncel kura kirpilir ve kullaniciya sebebi soylenir.
   */
  const kurDegistir = deger => {
    const girilen = sayiyaCevir(deger);

    if (girilen > guncelKur) {
      setKurUyarisi(
        `Kur, TCMB güncel kurunun (${guncelKur.toFixed(4)}) üzerine çıkamaz. Değer güncel kura çekildi.`
      );
      setForm(onceki => ({ ...onceki, DovizKuru: guncelKur }));
      return;
    }

    setKurUyarisi('');
    setForm(onceki => ({ ...onceki, DovizKuru: deger }));
  };

  /** Ekranda gosterilecek tutar: katalog TL, gosterim secili para biriminde. */
  const goster = tutarTL =>
    tlMi
      ? tutarFormatla(tutarTL, 'TRY')
      : tutarFormatla(tldenCevir(tutarTL, form.DovizKuru), form.ParaBirimi);

  // ------------------------------------------------------------- KALEMLER
  const kalemEkle = () => {
    setForm(onceki => ({
      ...onceki,
      Kalemler: [
        ...onceki.Kalemler,
        { SatirId: Date.now(), KalemId: null, Miktar: 1 }
      ]
    }));
  };

  const kalemDegistir = (satirId, alan, deger) => {
    setForm(onceki => ({
      ...onceki,
      Kalemler: onceki.Kalemler.map(s =>
        s.SatirId === satirId ? { ...s, [alan]: deger } : s
      )
    }));
  };

  const kalemSil = satirId => {
    setForm(onceki => ({
      ...onceki,
      Kalemler: onceki.Kalemler.filter(s => s.SatirId !== satirId)
    }));
  };

  const katalogBul = kalemId => katalog.find(k => k.Id === kalemId);

  const toplamlar = useMemo(() => toplamlariHesapla(form, katalog), [form, katalog]);

  // Autocomplete groupBy ardisik gruplar bekliyor; katalog gruba gore siralaniyor.
  const siraliKatalog = useMemo(
    () =>
      [...katalog].sort(
        (a, b) =>
          String(a.Grup || '').localeCompare(String(b.Grup || ''), 'tr') ||
          String(a.Aciklama || '').localeCompare(String(b.Aciklama || ''), 'tr')
      ),
    [katalog]
  );

  // -------------------------------------------------------------- KAYDET
  const dogrula = () => {
    const yeni = {};
    if (!String(form.ProjeAdi || '').trim()) yeni.ProjeAdi = 'Proje adı zorunludur.';
    if (!form.BayiId) yeni.BayiId = 'Bayi seçiniz.';
    if (!form.ServisId) yeni.ServisId = 'Servis seçiniz.';
    if (!String(form.MusteriAdSoyad || '').trim())
      yeni.MusteriAdSoyad = 'Müşteri adı zorunludur.';
    if (!form.Kalemler.length) yeni.Kalemler = 'En az bir teklif kalemi eklemelisiniz.';
    else if (form.Kalemler.some(s => !s.KalemId))
      yeni.Kalemler = 'Kalem seçilmemiş satırlar var.';

    setHatalar(yeni);
    return Object.keys(yeni).length === 0;
  };

  const kaydet = () => {
    if (!dogrula()) {
      setHata('Lütfen işaretli alanları kontrol ediniz.');
      return;
    }

    setHata('');
    setKaydediliyor(true);

    const secilenBayi = bayiler.find(b => b.Id === form.BayiId);
    const secilenServis = servisler.find(s => s.Id === form.ServisId);

    teklifKaydet({
      ...form,
      Bayi: secilenBayi ? secilenBayi.Dsc : '',
      Servis: secilenServis ? secilenServis.Dsc : ''
    })
      .then(() => {
        setKaydediliyor(false);
        onSaved();
      })
      .catch(() => {
        setKaydediliyor(false);
        setHata('Teklif kaydedilemedi. Lütfen tekrar deneyin.');
      });
  };

  const kalemHatasi = hatalar.Kalemler;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleIcon}>
          <ReceiptLongIcon />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
            {guncellemeMi ? 'Teklif Güncelleme' : 'Yeni Teklif Oluşturma'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: C.inkMuted }}>
              {guncellemeMi && form.TeklifNo
                ? form.TeklifNo
                : 'Proje, müşteri ve kalem bilgilerini giriniz'}
            </Typography>
            {kopyaMi && (
              <Chip
                size="small"
                label="Kopyalandı"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: `${BRAND.main}1f`,
                  color: '#9a5b00'
                }}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {hata && (
          <Alert severity="warning" onClose={() => setHata('')} sx={{ mb: 2, borderRadius: '10px' }}>
            {hata}
          </Alert>
        )}

        {yukleniyor ? (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, gap: 2 }}>
            <CircularProgress sx={{ color: BRAND.main }} />
            <Typography variant="body2" sx={{ color: C.inkMuted }}>
              Teklif bilgileri yükleniyor...
            </Typography>
          </Box>
        ) : (
          <>
            {/* ---------------------------------------------- TEKLIF BILGILERI */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>Teklif Bilgileri</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Proje Adı"
                    placeholder="Örn: Kuzey Rezidans"
                    value={form.ProjeAdi}
                    onChange={e => alanDegistir('ProjeAdi', e.target.value)}
                    error={Boolean(hatalar.ProjeAdi)}
                    helperText={hatalar.ProjeAdi}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    size="small"
                    options={bayiler}
                    getOptionLabel={o => o.Dsc || ''}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    value={bayiler.find(b => b.Id === form.BayiId) || null}
                    onChange={(e, v) => alanDegistir('BayiId', v ? v.Id : null)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Bayi"
                        error={Boolean(hatalar.BayiId)}
                        helperText={hatalar.BayiId}
                        sx={styles.field}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    size="small"
                    options={servisler}
                    getOptionLabel={o => o.Dsc || ''}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    value={servisler.find(s => s.Id === form.ServisId) || null}
                    onChange={(e, v) => alanDegistir('ServisId', v ? v.Id : null)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Servis"
                        error={Boolean(hatalar.ServisId)}
                        helperText={hatalar.ServisId}
                        sx={styles.field}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Geçerlilik (gün)"
                    value={form.GecerlilikGun}
                    onChange={e => alanDegistir('GecerlilikGun', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>

                <Grid size={{ xs: 6, md: 3 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Para Birimi"
                    value={form.ParaBirimi}
                    onChange={e => paraBirimiDegistir(e.target.value)}
                    sx={styles.field}
                  >
                    {PARA_BIRIMLERI.map(pb => {
                      const Ikon = PARA_IKONLARI[pb.ikon];
                      return (
                        <MenuItem key={pb.kod} value={pb.kod}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Ikon sx={{ fontSize: '1.1rem', color: BRAND.main }} />
                            {pb.kod} — {pb.ad}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={tlMi ? 'Kur (TL için geçerli değil)' : `Kur — 1 ${form.ParaBirimi} = ? TL`}
                    value={tlMi ? 1 : form.DovizKuru}
                    disabled={tlMi}
                    onChange={e => kurDegistir(e.target.value)}
                    inputProps={{ min: 0, max: guncelKur, step: '0.0001' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="TCMB güncel kuruna çek" arrow>
                            <span>
                              <Button
                                size="small"
                                disabled={tlMi}
                                onClick={() => alanDegistir('DovizKuru', guncelKur)}
                                sx={{
                                  minWidth: 0,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  color: BRAND.main
                                }}
                              >
                                Güncel
                              </Button>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      tlMi
                        ? 'Teklif TL olarak hazırlanıyor, kur uygulanmaz.'
                        : `TCMB ${kurBilgisi.tarih || 'bugün'} kuru yazıldı: ${guncelKur.toFixed(
                            4
                          )} TL · kontrol edip düşürebilirsiniz, üzerine çıkılamaz`
                    }
                    sx={styles.field}
                  />
                </Grid>
              </Grid>

              {kurUyarisi && (
                <Alert
                  severity="info"
                  onClose={() => setKurUyarisi('')}
                  sx={{ mt: 2, borderRadius: '10px' }}
                >
                  {kurUyarisi}
                </Alert>
              )}
            </Box>

            {/* ---------------------------------------------- MUSTERI BILGILERI */}
            <Box sx={styles.section}>
              <Typography sx={styles.sectionTitle}>Müşteri Bilgileri</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ad Soyad"
                    value={form.MusteriAdSoyad}
                    onChange={e => alanDegistir('MusteriAdSoyad', e.target.value)}
                    error={Boolean(hatalar.MusteriAdSoyad)}
                    helperText={hatalar.MusteriAdSoyad}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telefon"
                    placeholder="05XX XXX XX XX"
                    value={form.MusteriTelefon}
                    onChange={e => alanDegistir('MusteriTelefon', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Adres"
                    value={form.MusteriAdres}
                    onChange={e => alanDegistir('MusteriAdres', e.target.value)}
                    sx={styles.field}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* ---------------------------------------------------- KALEMLER */}
            <Box sx={styles.section}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5
                }}
              >
                <Typography sx={{ ...styles.sectionTitle, marginBottom: 0 }}>
                  Teklif Kalemleri
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={kalemEkle}
                  sx={primaryButton}
                >
                  Kalem Ekle
                </Button>
              </Box>

              {kalemHatasi && (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '10px' }}>
                  {kalemHatasi}
                </Alert>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.headCell}>İş Kalemi</TableCell>
                    <TableCell sx={styles.headCell} align="right">
                      Birim Fiyat
                    </TableCell>
                    <TableCell sx={styles.headCell} align="center">
                      Birim
                    </TableCell>
                    <TableCell sx={styles.headCell} align="right">
                      Miktar
                    </TableCell>
                    <TableCell sx={styles.headCell} align="right">
                      Tutar
                    </TableCell>
                    <TableCell sx={styles.headCell} align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.Kalemler.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...styles.bodyCell, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: C.inkSoft, py: 3 }}>
                          Henüz kalem eklenmedi. <strong>Kalem Ekle</strong> ile başlayın.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {form.Kalemler.map(satir => {
                    const secili = katalogBul(satir.KalemId);
                    const tutar = secili
                      ? secili.BirimFiyat * sayiyaCevir(satir.Miktar)
                      : 0;

                    return (
                      <TableRow key={satir.SatirId}>
                        {/* Aranabilir + gruplu: katalog buyudukce duz listede kaybolmasin. */}
                        <TableCell sx={{ ...styles.bodyCell, minWidth: 300 }}>
                          <Autocomplete
                            size="small"
                            options={siraliKatalog}
                            groupBy={o => o.Grup || 'Diğer'}
                            getOptionLabel={o => o.Aciklama || ''}
                            isOptionEqualToValue={(o, v) => o.Id === v.Id}
                            value={secili || null}
                            onChange={(e, v) =>
                              kalemDegistir(satir.SatirId, 'KalemId', v ? v.Id : null)
                            }
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Kalem ara veya seç"
                                sx={styles.field}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Birim fiyat katalogdan gelir, bu ekranda degistirilemez.
                            Katalog TL tutuyor; gosterim secili para biriminde. */}
                        <TableCell sx={{ ...styles.bodyCell, whiteSpace: 'nowrap' }} align="right">
                          <Typography component="span" sx={{ color: C.inkMuted, fontSize: '0.875rem' }}>
                            {secili ? goster(secili.BirimFiyat) : '—'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={styles.bodyCell} align="center">
                          {secili ? (
                            <Chip
                              size="small"
                              label={secili.Birim}
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: C.headBg,
                                color: C.inkMuted
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>

                        <TableCell sx={{ ...styles.bodyCell, width: 140 }} align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={satir.Miktar}
                            onChange={e =>
                              kalemDegistir(satir.SatirId, 'Miktar', e.target.value)
                            }
                            inputProps={{ min: 0, style: { textAlign: 'right' } }}
                            sx={styles.field}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            ...styles.bodyCell,
                            whiteSpace: 'nowrap',
                            fontWeight: 700,
                            color: C.ink
                          }}
                          align="right"
                        >
                          {goster(tutar)}
                        </TableCell>

                        <TableCell sx={styles.bodyCell} align="center">
                          <Tooltip title="Satırı sil" arrow>
                            <IconButton
                              size="small"
                              onClick={() => kalemSil(satir.SatirId)}
                              sx={ACTION_SX.danger}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* ---------------------------------------------------- TOPLAMLAR */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ ...styles.section, marginBottom: 0, height: '100%' }}>
                  <Typography sx={styles.sectionTitle}>Teklif Şartları</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    size="small"
                    placeholder="Teklife eklenecek şartlar ve bilgilendirme notları"
                    value={form.Sartlar}
                    onChange={e => alanDegistir('Sartlar', e.target.value)}
                    sx={styles.field}
                  />
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ ...styles.section, marginBottom: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5
                    }}
                  >
                    <Typography sx={{ ...styles.sectionTitle, marginBottom: 0 }}>
                      Toplamlar
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        tlMi
                          ? 'TRY'
                          : `${form.ParaBirimi} · 1 = ${sayiyaCevir(form.DovizKuru).toFixed(4)} TL`
                      }
                      sx={{
                        height: 24,
                        borderRadius: '7px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: `${BRAND.main}1f`,
                        color: '#9a5b00'
                      }}
                    />
                  </Box>

                  <Box sx={styles.ozetSatir}>
                    <span>Ara Toplam</span>
                    <strong>{goster(toplamlar.araToplam)}</strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Sarf Malzeme ve Genel Giderler</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.SarfMalzemeOrani}
                        onChange={e => alanDegistir('SarfMalzemeOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong>{goster(toplamlar.sarf)}</strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Özel İndirim</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.IndirimOrani}
                        onChange={e => alanDegistir('IndirimOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong style={{ color: BRAND.danger }}>
                      -{goster(toplamlar.indirim)}
                    </strong>
                  </Box>
                  <Divider />

                  <Box sx={styles.ozetSatir}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>KDV</span>
                      <TextField
                        size="small"
                        type="number"
                        value={form.KdvOrani}
                        onChange={e => alanDegistir('KdvOrani', e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ ...styles.field, width: 96 }}
                      />
                    </Box>
                    <strong>{goster(toplamlar.kdv)}</strong>
                  </Box>

                  <Box sx={styles.ozetToplam}>
                    <Typography sx={{ fontWeight: 700, color: C.ink }}>GENEL TOPLAM</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#9a5b00' }}>
                      {goster(toplamlar.genelToplam)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        {/* Genel toplam her zaman gorunur; asagi kaydirmaya gerek kalmiyor. */}
        <Box sx={{ mr: 'auto', display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: C.inkMuted }}>
            GENEL TOPLAM
          </Typography>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#9a5b00' }}>
            {goster(toplamlar.genelToplam)}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={onClose} sx={cancelButton} disabled={kaydediliyor}>
          Vazgeç
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={neutralButton}
          endIcon={<CloseIcon />}
          disabled={kaydediliyor}
        >
          Kapat
        </Button>
        <Button
          variant="contained"
          onClick={kaydet}
          sx={primaryButton}
          disabled={kaydediliyor || yukleniyor}
          startIcon={
            kaydediliyor ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />
          }
        >
          {kaydediliyor ? 'Kaydediliyor' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

OfferDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'update']).isRequired,
  teklifId: PropTypes.number,
  kopyaKaynakId: PropTypes.number,
  bayiler: PropTypes.array,
  servisler: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

OfferDialog.defaultProps = {
  teklifId: 0,
  kopyaKaynakId: 0,
  bayiler: [],
  servisler: []
};

export default OfferDialog;
